import ArrowSVG from '@public/icons/arr.svg';
import ArrSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Partner } from '@/entities/partner';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import OrganizeDetailPopup, {
    ORGANIZE_DETAIL_POPUP,
} from '@/shared/components/popups/OrganizeDetailPopup/OrganizeDetailPopup';
import OrganizeFormPopup from '@/shared/components/popups/OrganizeFormPopup';
import { ORGANIZE_FORM_POPUP } from '@/shared/components/popups/OrganizeFormPopup/OrganizeFormPopup';
import Button from '@/shared/components/shared/Button';
import Dropdown from '@/shared/components/shared/Dropdown';
import Image from '@/shared/components/shared/Image';
import Responsive from '@/shared/components/shared/Responsive';
import { Theme } from '@/shared/lib/types';
import OrganizeCategoryItem from '../../shared/OrganizeCategoryList';
import Typografed from '../../shared/Typografed';

export type OrganizeSectionProps = {
    title: string;
    categories: {
        title: string;
        description?: string;
        items: Partner[];
    }[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    organizeSectionProps: OrganizeSectionProps;
    'data-theme'?: Theme;
    'data-bg'?: string;
};

export const SECTION_ID = 'organize';

const OrganizeSection = ({ organizeSectionProps, ...props }: Props) => {
    const sliderRef = useRef<SwiperRef>(null);

    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [activePartner, setActivePartner] = useState<Partner | null>(null);
    const { openPopup } = useSetOpenedPopupsState();
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';

    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const onResize = () => {
            if (sliderRef.current) {
                const wrapperEl = (sliderRef.current as unknown as HTMLElement).querySelector<HTMLElement>(
                    '.swiper-wrapper',
                );

                if (wrapperEl) {
                    setIsOverflowing(wrapperEl.scrollWidth > wrapperEl.offsetWidth);
                }
            }
        };

        onResize();
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [sliderRef]);

    const totalItemsCount = organizeSectionProps.categories.reduce(
        (total, category) => total + category.items.length,
        0,
    );

    const filteredCategories = activeCategory
        ? organizeSectionProps.categories.filter((category) => category.title === activeCategory)
        : organizeSectionProps.categories;

    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

    const handleActivePartner = (partner: Partner | null) => {
        setSelectedPartner(partner);
        openPopup(ORGANIZE_DETAIL_POPUP);
    };

    return (
        <section
            {...props}
            className={classNames(
                'organize-section-wrapper wrapper js-header-theme-trigger js-bg-trigger',
                props.className,
            )}
            data-bg={props['data-bg'] ?? '#fff'}
            data-theme={props['data-theme'] ?? 'dark'}
            id={props.id ?? SECTION_ID}
        >
            <div className="organize-section-title h3">
                <Typografed>{organizeSectionProps.title}</Typografed>
            </div>
            <div className="organize-section-content">
                {!isDesktop ? (
                    <div className="organize-section-content-top-mobile">
                        <Dropdown className={'organize-select'}>
                            <Dropdown.Toggler className="organize-select-btn">
                                <button className="text-xs">{activeCategory || 'Все'}</button>
                                <ArrowSVG className="organize-arrow-svg" />
                            </Dropdown.Toggler>
                            <Dropdown.Content className="organize-filter__content" position="bottom-right">
                                <ul className="list-unstyled menu-dropdown-list">
                                    <li>
                                        <Dropdown.Picker
                                            className={classNames('organize-select-item text-xs', {
                                                'is-active': !activeCategory,
                                            })}
                                            onClick={() => setActiveCategory(null)}
                                        >
                                            <div className="organize-section-category-name text-xs">Все</div>
                                            <div className="organize-section-category-count text-xs">
                                                {totalItemsCount}
                                            </div>
                                        </Dropdown.Picker>
                                    </li>
                                    {organizeSectionProps.categories.map((item, i) => (
                                        <li key={i}>
                                            <Dropdown.Picker
                                                className={classNames('organize-select-item text-xs', {
                                                    'is-active': activeCategory === item.title,
                                                })}
                                                onClick={() => setActiveCategory(item.title)}
                                            >
                                                <div className="organize-section-category-name text-xs">
                                                    {item.title}
                                                </div>
                                                <div className="organize-section-category-count text-xs">
                                                    {item.items.length}
                                                </div>
                                            </Dropdown.Picker>
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown.Content>
                        </Dropdown>
                        <Button
                            onClick={() => {
                                openPopup(ORGANIZE_FORM_POPUP);
                            }}
                            className="organize-left-btn"
                            variant="outline-primary"
                        >
                            Стать партнером
                        </Button>
                    </div>
                ) : (
                    <div className="organize-section-content-left">
                        <div className="organize-section-categories">
                            <div
                                className={classNames('organize-section-category-item', {
                                    'is-active': !activeCategory,
                                })}
                                onClick={() => setActiveCategory(null)}
                            >
                                <div className="organize-section-category-name text-xs">Все</div>
                                <div className="organize-section-category-count text-xs">{totalItemsCount}</div>
                            </div>
                            {organizeSectionProps.categories.map((category, i) => (
                                <div
                                    className={classNames('organize-section-category-item', {
                                        'is-active': activeCategory === category.title,
                                    })}
                                    key={i}
                                    onClick={() => setActiveCategory(category.title)}
                                >
                                    <div className="organize-section-category-name text-xs">{category.title}</div>
                                    <div className="organize-section-category-count text-xs">
                                        {category.items.length}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button
                            onClick={() => {
                                openPopup(ORGANIZE_FORM_POPUP);
                            }}
                            className="organize-left-btn"
                            variant="outline-primary"
                        >
                            Стать партнером
                        </Button>
                    </div>
                )}
                <AnimatePresence mode="wait">
                    <motion.div
                        className="organize-section-content-right"
                        variants={{
                            initial: {
                                opacity: 0,
                            },
                            animate: {
                                opacity: 1,
                            },
                            exit: {
                                opacity: 0,
                            },
                        }}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        key={activeCategory}
                    >
                        {filteredCategories.map((category, i) => (
                            <div className="organize-content-category-block" key={i}>
                                <div className="organize-content-category-block-top">
                                    <div className="category-title text-xl">{category.title}</div>
                                    {category.description && (
                                        <div className="category-description text-s">{category.description}</div>
                                    )}
                                </div>
                                <OrganizeCategoryItem
                                    partners={category.items}
                                    onSelectPartner={handleActivePartner}
                                    key={i}
                                />
                            </div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>
            <OrganizeDetailPopup partner={selectedPartner} />
            <OrganizeFormPopup />
        </section>
    );
};

export default OrganizeSection;
